/**
 * Machine d'état pour les campagnes
 * Gère les transitions d'état valides et les règles métier associées
 * Conformément aux exigences 1.3, 1.4, et 2.2
 */

import { CampaignStatus } from '../../types/states';
import { StateTransitionError, BusinessRuleViolationError } from '../../errors/BusinessErrors';

/**
 * Contexte pour les transitions d'état des campagnes
 */
export interface CampaignTransitionContext {
  campaignId: string;
  userId?: string;
  reason?: string;
  referentCount?: number;
  completedReferentCount?: number;
  candidateResponse?: 'accepted' | 'refused';
}

/**
 * Résultat d'une validation de transition
 */
export interface TransitionValidationResult {
  isValid: boolean;
  reason?: string;
  requiredConditions?: string[];
}

/**
 * Machine d'état pour les campagnes
 * Définit les transitions autorisées et valide les règles métier
 */
export class CampaignStateMachine {
  /**
   * Définition des transitions autorisées entre états
   * Chaque état source peut transitionner vers les états listés
   */
  private static readonly transitions: Record<CampaignStatus, CampaignStatus[]> = {
    [CampaignStatus.BROUILLON]: [
      CampaignStatus.EN_ATTENTE_CANDIDAT,
      CampaignStatus.ANNULEE
    ],
    [CampaignStatus.EN_ATTENTE_CANDIDAT]: [
      CampaignStatus.EN_ATTENTE_REFERENTS,
      CampaignStatus.REFUSE,
      CampaignStatus.ANNULEE
    ],
    [CampaignStatus.EN_ATTENTE_REFERENTS]: [
      CampaignStatus.TERMINE,
      CampaignStatus.ANNULEE
    ],
    [CampaignStatus.TERMINE]: [],
    [CampaignStatus.ANNULEE]: [],
    [CampaignStatus.REFUSE]: []
  };

  /**
   * Conditions métier requises pour certaines transitions
   */
  private static readonly transitionConditions: Record<string, (context: CampaignTransitionContext) => TransitionValidationResult> = {
    [`${CampaignStatus.BROUILLON}->${CampaignStatus.EN_ATTENTE_CANDIDAT}`]: (context) => {
      // Une campagne doit avoir au moins un référent configuré
      if (!context.referentCount || context.referentCount === 0) {
        return {
          isValid: false,
          reason: 'La campagne doit avoir au moins un référent configuré',
          requiredConditions: ['Au moins un référent doit être ajouté à la campagne']
        };
      }
      return { isValid: true };
    },

    [`${CampaignStatus.EN_ATTENTE_CANDIDAT}->${CampaignStatus.EN_ATTENTE_REFERENTS}`]: (context) => {
      // Le candidat doit avoir accepté de participer
      if (context.candidateResponse !== 'accepted') {
        return {
          isValid: false,
          reason: 'Le candidat doit accepter de participer avant d\'envoyer les invitations aux référents',
          requiredConditions: ['Acceptation du candidat requise']
        };
      }
      return { isValid: true };
    },

    [`${CampaignStatus.EN_ATTENTE_CANDIDAT}->${CampaignStatus.REFUSE}`]: (context) => {
      // Le candidat doit avoir refusé de participer
      if (context.candidateResponse !== 'refused') {
        return {
          isValid: false,
          reason: 'Cette transition nécessite un refus explicite du candidat',
          requiredConditions: ['Refus du candidat requis']
        };
      }
      return { isValid: true };
    },

    [`${CampaignStatus.EN_ATTENTE_REFERENTS}->${CampaignStatus.TERMINE}`]: (context) => {
      // Tous les référents doivent avoir terminé leurs évaluations
      if (context.referentCount === undefined || context.completedReferentCount === undefined) {
        return {
          isValid: false,
          reason: 'Impossible de déterminer le statut des référents',
          requiredConditions: ['Informations sur les référents requises']
        };
      }

      if (context.completedReferentCount < context.referentCount) {
        return {
          isValid: false,
          reason: `Tous les référents doivent terminer leurs évaluations (${context.completedReferentCount}/${context.referentCount} terminés)`,
          requiredConditions: ['Toutes les évaluations de référents doivent être complétées']
        };
      }

      return { isValid: true };
    }
  };

  /**
   * Vérifie si une transition est autorisée au niveau de la machine d'état
   * @param fromState État source
   * @param toState État cible
   * @returns true si la transition est autorisée, false sinon
   */
  static canTransition(fromState: CampaignStatus, toState: CampaignStatus): boolean {
    const allowedTransitions = this.transitions[fromState];
    return allowedTransitions?.includes(toState) ?? false;
  }

  /**
   * Retourne la liste des états vers lesquels une transition est possible
   * @param currentState État actuel
   * @returns Liste des états de destination possibles
   */
  static getValidTransitions(currentState: CampaignStatus): CampaignStatus[] {
    return this.transitions[currentState] ?? [];
  }

  /**
   * Valide une transition avec les règles métier
   * @param fromState État source
   * @param toState État cible
   * @param context Contexte de la transition
   * @returns Résultat de la validation
   */
  static validateTransition(
    fromState: CampaignStatus,
    toState: CampaignStatus,
    context: CampaignTransitionContext
  ): TransitionValidationResult {
    // Vérification de base de la transition
    if (!this.canTransition(fromState, toState)) {
      return {
        isValid: false,
        reason: `Transition non autorisée de ${fromState} vers ${toState}`,
        requiredConditions: [`L'état ${fromState} ne peut pas transitionner vers ${toState}`]
      };
    }

    // Vérification des conditions métier spécifiques
    const transitionKey = `${fromState}->${toState}`;
    const conditionValidator = this.transitionConditions[transitionKey];

    if (conditionValidator) {
      return conditionValidator(context);
    }

    // Transition autorisée sans conditions spéciales
    return { isValid: true };
  }

  /**
   * Effectue une transition d'état avec validation complète
   * @param fromState État source
   * @param toState État cible
   * @param context Contexte de la transition
   * @throws StateTransitionError si la transition n'est pas autorisée
   * @throws BusinessRuleViolationError si les règles métier ne sont pas respectées
   */
  static executeTransition(
    fromState: CampaignStatus,
    toState: CampaignStatus,
    context: CampaignTransitionContext
  ): void {
    const validation = this.validateTransition(fromState, toState, context);

    if (!validation.isValid) {
      if (validation.reason?.includes('non autorisée')) {
        throw new StateTransitionError(
          fromState,
          toState,
          'campaign',
          context
        );
      } else {
        throw new BusinessRuleViolationError(
          `Transition ${fromState} -> ${toState}`,
          validation.reason || 'Conditions métier non respectées',
          {
            ...context,
            requiredConditions: validation.requiredConditions
          }
        );
      }
    }
  }

  /**
   * Détermine automatiquement le prochain état basé sur le contexte
   * Utilisé pour la progression automatique des campagnes
   * @param currentState État actuel
   * @param context Contexte de la campagne
   * @returns Prochain état suggéré ou null si aucune progression automatique n'est possible
   */
  static getAutoProgressionState(
    currentState: CampaignStatus,
    context: CampaignTransitionContext
  ): CampaignStatus | null {
    switch (currentState) {
      case CampaignStatus.EN_ATTENTE_CANDIDAT:
        if (context.candidateResponse === 'accepted') {
          return CampaignStatus.EN_ATTENTE_REFERENTS;
        }
        if (context.candidateResponse === 'refused') {
          return CampaignStatus.REFUSE;
        }
        break;

      case CampaignStatus.EN_ATTENTE_REFERENTS:
        if (
          context.referentCount && 
          context.completedReferentCount && 
          context.completedReferentCount >= context.referentCount
        ) {
          return CampaignStatus.TERMINE;
        }
        break;

      default:
        // Pas de progression automatique pour les autres états
        break;
    }

    return null;
  }

  /**
   * Vérifie si un état est terminal (aucune transition possible)
   * @param state État à vérifier
   * @returns true si l'état est terminal
   */
  static isTerminalState(state: CampaignStatus): boolean {
    return this.getValidTransitions(state).length === 0;
  }

  /**
   * Vérifie si un état permet la modification des données de campagne
   * @param state État à vérifier
   * @returns true si les modifications sont autorisées
   */
  static allowsModification(state: CampaignStatus): boolean {
    return state === CampaignStatus.BROUILLON;
  }

  /**
   * Vérifie si un état permet la génération de rapports
   * @param state État à vérifier
   * @returns true si la génération de rapports est autorisée
   */
  static allowsReportGeneration(state: CampaignStatus): boolean {
    return state === CampaignStatus.TERMINE;
  }

  /**
   * Retourne une description lisible de l'état
   * @param state État à décrire
   * @returns Description de l'état
   */
  static getStateDescription(state: CampaignStatus): string {
    const descriptions: Record<CampaignStatus, string> = {
      [CampaignStatus.BROUILLON]: 'La campagne est en cours de préparation et peut être modifiée',
      [CampaignStatus.EN_ATTENTE_CANDIDAT]: 'En attente de la réponse du candidat pour démarrer le processus',
      [CampaignStatus.EN_ATTENTE_REFERENTS]: 'Les référents ont été invités et leurs évaluations sont en cours',
      [CampaignStatus.TERMINE]: 'Toutes les évaluations sont terminées, les rapports peuvent être générés',
      [CampaignStatus.ANNULEE]: 'La campagne a été annulée et ne peut plus être modifiée',
      [CampaignStatus.REFUSE]: 'Le candidat a refusé de participer à la campagne'
    };

    return descriptions[state] || 'État inconnu';
  }

  /**
   * Retourne les actions possibles pour un état donné
   * @param state État actuel
   * @returns Liste des actions possibles
   */
  static getAvailableActions(state: CampaignStatus): string[] {
    const actions: Record<CampaignStatus, string[]> = {
      [CampaignStatus.BROUILLON]: [
        'Modifier la campagne',
        'Ajouter/supprimer des référents',
        'Activer la campagne',
        'Annuler la campagne'
      ],
      [CampaignStatus.EN_ATTENTE_CANDIDAT]: [
        'Relancer le candidat',
        'Annuler la campagne'
      ],
      [CampaignStatus.EN_ATTENTE_REFERENTS]: [
        'Relancer les référents',
        'Voir le progrès des évaluations',
        'Annuler la campagne'
      ],
      [CampaignStatus.TERMINE]: [
        'Générer un rapport',
        'Télécharger les résultats',
        'Voir les évaluations'
      ],
      [CampaignStatus.ANNULEE]: [
        'Voir les détails de la campagne'
      ],
      [CampaignStatus.REFUSE]: [
        'Voir les détails de la campagne'
      ]
    };

    return actions[state] || [];
  }
}